import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import {
  ArrowForward,
  CheckCircle,
  Key,
  Mail,
  Person,
  ReceiptLong,
  Shield,
  ShowChart,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useRecaptcha } from '../hooks/useRecaptcha';

interface TopStepSignupForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  topstepAccountEmail: string;
  topstepApiKey: string;
  acceptTerms: boolean;
}

interface CheckoutEngagement {
  id: string;
  title: string;
  type: string;
  amount_cents: number;
  payment_url?: string | null;
}

const initialForm: TopStepSignupForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
  topstepAccountEmail: '',
  topstepApiKey: '',
  acceptTerms: false,
};

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

const TopStepBotSignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { executeRecaptcha } = useRecaptcha();
  const [form, setForm] = useState<TopStepSignupForm>(initialForm);
  const [showPassword, setShowPassword] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signupComplete, setSignupComplete] = useState(false);
  const [engagements, setEngagements] = useState<CheckoutEngagement[]>([]);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [provisionMessage, setProvisionMessage] = useState<string | null>(null);

  const sortedEngagements = useMemo(() => {
    return [...engagements].sort((a, b) => {
      if (a.type === b.type) return 0;
      return a.type === 'one_time' ? -1 : 1;
    });
  }, [engagements]);

  const updateField = (field: keyof TopStepSignupForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validate = (): boolean => {
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      setError('Name and email are required.');
      return false;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }
    if (!(form.topstepAccountEmail || form.email).includes('@')) {
      setError('Enter a valid TopStep account email.');
      return false;
    }
    if (form.topstepApiKey.trim().length < 12) {
      setError('Enter your TopStep ProjectX API key.');
      return false;
    }
    if (!form.acceptTerms) {
      setError('You must accept the terms before checkout.');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    setError(null);
    setProvisionMessage(null);

    try {
      const recaptchaToken = await executeRecaptcha('REGISTER');
      if (!recaptchaToken) throw new Error('Security verification failed. Please try again.');

      const response = await fetch('/api/customers/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: form.email.trim(),
          password: form.password,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          phone: form.phone.trim() || undefined,
          companyName: `${form.firstName.trim()} ${form.lastName.trim()} TopStep Bot`,
          website: '',
          industry: 'Trading Automation',
          companySize: '1',
          tierId: 'topstep-bot',
          billingCycle: 'monthly',
          addOns: [],
          topstepAccountEmail: (form.topstepAccountEmail || form.email).trim(),
          topstepApiKey: form.topstepApiKey.trim(),
          agreedToTerms: form.acceptTerms,
          recaptchaToken,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || 'Signup failed.');
      }

      setRegisteredEmail(form.email.trim());
      setEngagements(data.engagements || []);
      setSignupComplete(true);
      if (data.topstepProvisioning?.status !== 'ready' && data.topstepProvisioning?.message) {
        setProvisionMessage(data.topstepProvisioning.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed.');
    } finally {
      setLoading(false);
    }
  };

  if (signupComplete) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#0B0B0D', py: { xs: 4, md: 8 } }}>
        <Container maxWidth="md">
          <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 1, bgcolor: '#161616', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Stack spacing={3} alignItems="center" textAlign="center">
              <CheckCircle sx={{ fontSize: 64, color: '#4CAF50' }} />
              <Box>
                <Typography variant="h4" fontWeight={900} color="white" gutterBottom>
                  Checkout Is Ready
                </Typography>
                <Typography color="grey.400" sx={{ maxWidth: 560 }}>
                  Your account was created for {registeredEmail}. Complete the setup retainer and monthly subscription to unlock the bot dashboard.
                </Typography>
              </Box>

              {provisionMessage && (
                <Alert severity="warning" sx={{ width: '100%', textAlign: 'left' }}>
                  Credentials were saved, but ProjectX verification needs review: {provisionMessage}
                </Alert>
              )}

              {sortedEngagements.length === 0 ? (
                <Alert severity="info" sx={{ width: '100%', textAlign: 'left' }}>
                  Your account was created. Payment links will be sent to your inbox as soon as the portal checkout service is available.
                </Alert>
              ) : (
              <Grid container spacing={2}>
                {sortedEngagements.map((engagement) => (
                  <Grid item xs={12} md={6} key={engagement.id}>
                    <Box sx={{ p: 2.5, height: '100%', borderRadius: 1, bgcolor: alpha('#fff', 0.04), border: '1px solid rgba(255,255,255,0.08)', textAlign: 'left' }}>
                      <Stack spacing={1.5}>
                        <Chip
                          icon={<ReceiptLong sx={{ fontSize: 16 }} />}
                          label={engagement.type === 'one_time' ? 'Upfront Retainer' : 'Monthly Subscription'}
                          size="small"
                          sx={{ alignSelf: 'flex-start', color: '#F44336', borderColor: alpha('#F44336', 0.4) }}
                          variant="outlined"
                        />
                        <Typography fontWeight={800} color="white">
                          {engagement.title}
                        </Typography>
                        <Typography variant="h4" fontWeight={900} color="white">
                          {formatCents(engagement.amount_cents)}
                        </Typography>
                        <Button
                          href={engagement.payment_url || undefined}
                          disabled={!engagement.payment_url}
                          variant="contained"
                          endIcon={<ArrowForward />}
                          sx={{ bgcolor: '#F44336', '&:hover': { bgcolor: '#D32F2F' } }}
                        >
                          Checkout
                        </Button>
                      </Stack>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="outlined" onClick={() => navigate('/login')} sx={{ borderColor: alpha('#fff', 0.24), color: 'white' }}>
                  Sign In
                </Button>
                <Button component={RouterLink} to="/pricing" sx={{ color: 'grey.300' }}>
                  Back to REDEX
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0B0B0D', color: 'white', py: { xs: 3, md: 6 } }}>
      <Container maxWidth="lg">
        <Stack spacing={4}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" fontWeight={900}>REDEX</Typography>
            <Button component={RouterLink} to="/login" variant="outlined" sx={{ borderColor: alpha('#fff', 0.24), color: 'white' }}>
              Sign In
            </Button>
          </Box>

          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} md={5}>
              <Box sx={{ height: '100%', p: { xs: 0, md: 2 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Chip label="TopStep Bot Access" size="small" sx={{ alignSelf: 'flex-start', mb: 2, bgcolor: alpha('#F44336', 0.14), color: '#FF8A80', border: `1px solid ${alpha('#F44336', 0.26)}` }} />
                <Typography variant="h3" fontWeight={900} sx={{ mb: 2, letterSpacing: 0 }}>
                  Start the bot on your master TopStep account.
                </Typography>
                <Typography color="grey.400" sx={{ mb: 3, fontSize: '1rem', lineHeight: 1.7 }}>
                  $1,000 upfront, then $100/month. Use one ProjectX API key, run the bot on the master account, and copy trade the other TopStep accounts linked to it.
                </Typography>

                <Stack spacing={1.5}>
                  {[
                    { icon: <Key />, text: 'Provide your TopStep account email and ProjectX API key.' },
                    { icon: <ShowChart />, text: 'Track performance, positions, orders, and risk state from your dashboard.' },
                    { icon: <Shield />, text: 'Your dashboard is scoped to your login email and assigned account only.' },
                  ].map((item) => (
                    <Box key={item.text} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Box sx={{ color: '#F44336', mt: 0.25 }}>{item.icon}</Box>
                      <Typography color="grey.300">{item.text}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 1, bgcolor: '#161616', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="h5" fontWeight={900} color="white">
                      Create Your Bot Account
                    </Typography>
                    <Typography color="grey.500">
                      Checkout links are created immediately after signup.
                    </Typography>
                  </Box>

                  {error && <Alert severity="error">{error}</Alert>}

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField label="First Name" value={form.firstName} onChange={updateField('firstName')} fullWidth required />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Last Name" value={form.lastName} onChange={updateField('lastName')} fullWidth required />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                      <TextField
                        label="Account Email"
                        type="email"
                        value={form.email}
                        onChange={updateField('email')}
                        fullWidth
                        required
                        InputProps={{ startAdornment: <InputAdornment position="start"><Mail /></InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField label="Phone" value={form.phone} onChange={updateField('phone')} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        value={form.password}
                        onChange={updateField('password')}
                        fullWidth
                        required
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowPassword(prev => !prev)} edge="end">
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Confirm Password" type={showPassword ? 'text' : 'password'} value={form.confirmPassword} onChange={updateField('confirmPassword')} fullWidth required />
                    </Grid>
                  </Grid>

                  <Divider sx={{ borderColor: alpha('#fff', 0.08) }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="TopStep Account Email"
                        type="email"
                        value={form.topstepAccountEmail}
                        onChange={updateField('topstepAccountEmail')}
                        placeholder={form.email || 'name@example.com'}
                        fullWidth
                        InputProps={{ startAdornment: <InputAdornment position="start"><Person /></InputAdornment> }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="ProjectX API Key"
                        type={showApiKey ? 'text' : 'password'}
                        value={form.topstepApiKey}
                        onChange={updateField('topstepApiKey')}
                        fullWidth
                        required
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><Key /></InputAdornment>,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowApiKey(prev => !prev)} edge="end">
                                {showApiKey ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ p: 2, borderRadius: 1, bgcolor: alpha('#F44336', 0.08), border: `1px solid ${alpha('#F44336', 0.2)}` }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="grey.500">UPFRONT</Typography>
                        <Typography variant="h5" fontWeight={900}>$1,000</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="grey.500">RECURRING</Typography>
                        <Typography variant="h5" fontWeight={900}>$100/mo</Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  <FormControlLabel
                    control={<Checkbox checked={form.acceptTerms} onChange={updateField('acceptTerms')} />}
                    label={<Typography variant="body2" color="grey.300">I agree to the REDEX Terms of Service and authorize REDEX to provision the TopStep bot dashboard for my supplied account.</Typography>}
                  />

                  <Button
                    variant="contained"
                    size="large"
                    disabled={loading}
                    onClick={handleSubmit}
                    endIcon={loading ? <CircularProgress size={18} color="inherit" /> : <ArrowForward />}
                    sx={{ bgcolor: '#F44336', '&:hover': { bgcolor: '#D32F2F' }, py: 1.25 }}
                  >
                    Create Account and Checkout
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
};

export default TopStepBotSignupPage;